import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * One tooltip layer for the whole product. Any element with `data-tip`
 * (and optionally `data-kbd`, `data-tip-side`) gets a quiet label after a
 * short hover, or immediately on keyboard focus. Accessible names live on the
 * controls themselves; this layer is visual only.
 */

interface Tip {
  text: string;
  kbd?: string;
  rect: DOMRect;
  side: 'top' | 'bottom';
}

const HOVER_DELAY = 520;
const WARM_WINDOW = 700;

export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: HTMLElement | null = null;
    let lastHidden = 0;

    const read = (el: HTMLElement): Tip | null => {
      const text = el.dataset.tip;
      if (!text) return null;
      return { text, kbd: el.dataset.kbd, rect: el.getBoundingClientRect(), side: el.dataset.tipSide === 'bottom' ? 'bottom' : 'top' };
    };
    const hide = () => {
      clearTimeout(timer);
      if (current) lastHidden = performance.now();
      current = null;
      setTip(null);
    };
    const showFor = (el: HTMLElement, delay: number) => {
      clearTimeout(timer);
      current = el;
      const warm = performance.now() - lastHidden < WARM_WINDOW;
      timer = setTimeout(() => current === el && el.isConnected && setTip(read(el)), warm ? 0 : delay);
    };
    const target = (e: Event) => (e.target instanceof Element ? (e.target.closest('[data-tip]') as HTMLElement | null) : null);

    const over = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const el = target(e);
      if (el === current) return;
      if (!el) return hide();
      if (current) lastHidden = performance.now();
      setTip(null);
      showFor(el, HOVER_DELAY);
    };
    const focusIn = (e: FocusEvent) => {
      const el = target(e);
      if (el && el.matches(':focus-visible')) showFor(el, 0);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && hide();

    document.addEventListener('pointerover', over);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('keydown', key, true);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!tip || !el) return setPos(null);
    const { width, height } = el.getBoundingClientRect();
    const margin = 8;
    const gap = 8;
    const x = Math.min(window.innerWidth - width - margin, Math.max(margin, tip.rect.left + tip.rect.width / 2 - width / 2));
    let y = tip.side === 'top' ? tip.rect.top - height - gap : tip.rect.bottom + gap;
    if (y < margin) y = tip.rect.bottom + gap;
    if (y + height > window.innerHeight - margin) y = tip.rect.top - height - gap;
    setPos({ x, y });
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={ref}
      className="tooltip"
      data-side={tip.side}
      aria-hidden="true"
      style={pos ? { transform: `translate(${Math.round(pos.x)}px, ${Math.round(pos.y)}px)` } : { visibility: 'hidden' }}
    >
      <span>{tip.text}</span>
      {tip.kbd && <kbd>{tip.kbd}</kbd>}
    </div>
  );
}
